import { TableCellType, TableColumns } from '@teamsnap/snap-ui';
import { currencyGivenPennies } from 'core/utils';
import { startCase } from 'lodash';
import { DateTime } from 'luxon';
import * as React from 'react';
import { TransactionAction } from './TransactionAction';
import {
  Chargeback,
  PaymentTransaction,
  Refund,
  TransactionGroup,
  TransactionRow,
} from './types';

const displayPaymentMethod = (transaction) => {
  if (transaction.payment.paymentMethodAccountName === 'offline') {
    return `${startCase(transaction.payment.paymentMethodAccountName)} ${
      transaction.payment.paymentMethodAccountType
    }`;
  }

  return `${startCase(
    transaction.payment.paymentMethodAccountName
  )} ending in ${transaction.payment?.paymentMethodLast4}`;
};

export const formatRow = (
  transaction: TransactionGroup,
  showActions = true
): TransactionRow => ({
  ...transaction,
  id: `${transaction.id}`,
  type: 'Payment',
  status: startCase(transaction.status),
  date: transaction.payment?.date
    ? DateTime.fromJSDate(new Date(transaction.payment.date)).toFormat(
        'MM/dd/yyyy'
      )
    : '',
  paymentMethodType: displayPaymentMethod(transaction),
  amount: transaction.amount,
  appliedFeeAmount: transaction.feeAmount,
  netAmount: transaction.netAmount,
  showActions,
});

export const formatPaymentTransaction = (
  transaction: TransactionGroup,
  showActions = true
): PaymentTransaction => ({
    id: transaction.payment?.id,
    type: startCase(transaction.payment?.type),
    status: startCase(
      transaction.payment?.status === 'success'
        ? 'succeeded'
        : transaction.payment?.status
    ),
    date: transaction.payment?.date
      ? DateTime.fromJSDate(new Date(transaction.payment.date)).toFormat(
          'MM/dd/yyyy'
        )
      : '',
    paymentMethodType: displayPaymentMethod(transaction),
    amount: Number(transaction.payment?.amount),
    appliedFeeAmount: Number(transaction.payment?.appliedFeeAmount),
    netAmount: Number(transaction.payment?.netAmount),
    description: '',
    paymentMethodAccountName: '',
    paymentMethodAccountType: '',
    paymentMethodLast4: '',
    showActions,
  });

export const formatRefundRow = (
  refund: Refund,
  transaction: TransactionGroup,
  showActions = true
): TransactionRow => ({
  ...transaction,
  id: `${refund.id}`,
  type: 'Refund',
  status: startCase(refund.status),
  date: refund.insertedAt
    ? DateTime.fromJSDate(new Date(refund.insertedAt)).toFormat('MM/dd/yyyy')
    : '',
  paymentMethodType: refund.processorRefundId
    ? displayPaymentMethod(transaction)
    : 'Offline',
  amount: refund.amount * -1,
  appliedFeeAmount: refund.refundedFeeAmount * -1,
  netAmount: refund.netAmount,
  showActions,
});

export const formatChargebackRow = (
  chargeback: Chargeback,
  transaction: TransactionGroup,
  showActions = true
): TransactionRow => ({
  ...transaction,
  id: `${chargeback.id}`,
  type: 'Dispute',
  status: startCase(
    chargeback.status === 'success' ? 'refunded' : chargeback.status
  ),
  date: chargeback.insertedAt
    ? DateTime.fromJSDate(new Date(chargeback.insertedAt)).toFormat(
        'MM/dd/yyyy'
      )
    : '',
  paymentMethodType: displayPaymentMethod(transaction),
  amount: chargeback.amount * -1,
  appliedFeeAmount: 0,
  netAmount: chargeback.amount * -1,
  showActions,
});

export const getColumns = (
  handleRefund: (transaction: TransactionRow) => void
): TableColumns => ({
  id: {
    label: 'Transaction ID',
    sort: 'alpha',
    align: 'left',
    getBodyProps: (row) => ({
      type: TableCellType.Custom,
      children: row.id,
      height: 'tall',
    }),
  },
  type: {
    label: 'Transaction Type',
    sort: 'alpha',
    align: 'left',
  },
  status: {
    label: 'Status',
    sort: 'alpha',
    align: 'left',
  },
  date: {
    label: 'Date',
    sort: 'date',
    align: 'left',
  },
  paymentMethodType: {
    label: 'Payment Method',
    sort: 'alpha',
    align: 'left',
  },
  amount: {
    label: 'Charged Amount',
    sort: 'numeric',
    align: 'left',
    getBodyProps: (transaction) => ({
      type: TableCellType.Custom,
      children: currencyGivenPennies(transaction.amount),
    }),
  },
  appliedFeeAmount: {
    label: 'Applied Fee Amount',
    sort: 'numeric',
    align: 'left',
    getBodyProps: (transaction) => ({
      type: TableCellType.Custom,
      children: currencyGivenPennies(transaction.appliedFeeAmount),
    }),
  },
  netAmount: {
    label: 'Net Amount',
    sort: 'numeric',
    align: 'left',
    getBodyProps: (transaction) => ({
      type: TableCellType.Custom,
      children: currencyGivenPennies(transaction.netAmount),
    }),
  },
  actions: {
    label: '',
    align: 'center',
    getBodyProps: (transaction: TransactionRow) => ({
      type: 'custom',
      children: transaction.showActions ? (
        <TransactionAction
          transaction={transaction}
          handleRefund={handleRefund}
        />
      ) : null,
    }),
  },
});
